import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, FileText, TrendingUp, Calendar, Sparkles, Download, RefreshCw, Baby, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const WeeklyReports = () => {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [report, setReport] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchChildAndData();
  }, [childId]);

  const fetchChildAndData = async () => {
    try {
      // Fetch all children to find the current one
      const childrenResponse = await axios.get(`${API_BASE}/children`);
      const currentChild = childrenResponse.data.find(c => c.id === childId);
      setChild(currentChild);

      // Fetch activities for this child
      const activitiesResponse = await axios.get(`${API_BASE}/activities/${childId}`);
      const allActivities = activitiesResponse.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivities(allActivities);

      // Generate initial report
      await generateWeeklyReport();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const generateWeeklyReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API_BASE}/reports/weekly/${childId}`);
      setReport(response.data.report);
    } catch (error) {
      console.error('Report generation error:', error);
      if (error.response?.status === 404) {
        setReport('AI reporting is not yet configured. Please contact an admin to set up OpenRouter integration.');
      } else {
        setReport('Unable to generate report at this time. Please try again later.');
      }
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} months`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years}y ${months}m` : `${years} years`;
    }
  };

  const getRecentActivities = () => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return activities.filter(activity => new Date(activity.date) >= weekAgo);
  };

  const getMilestoneStats = () => {
    const recentActivities = getRecentActivities();
    const milestoneTypes = {};
    
    recentActivities.forEach(activity => {
      if (activity.milestone_category) {
        const category = activity.milestone_category.split('.')[0].trim(); // Take first part before period
        milestoneTypes[category] = (milestoneTypes[category] || 0) + 1;
      }
    });
    
    return Object.entries(milestoneTypes)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5); // Top 5 categories
  };

  const getMilestoneColor = (category) => {
    const colors = {
      'Motor': 'bg-blue-100 text-blue-700 border-blue-200',
      'Cognitive': 'bg-purple-100 text-purple-700 border-purple-200',
      'Language': 'bg-green-100 text-green-700 border-green-200',
      'Social': 'bg-pink-100 text-pink-700 border-pink-200',
      'Emotional': 'bg-amber-100 text-amber-700 border-amber-200',
      'Physical': 'bg-red-100 text-red-700 border-red-200',
      'General Development': 'bg-slate-100 text-slate-700 border-slate-200'
    };
    
    const matchedCategory = Object.keys(colors).find(key => 
      category && category.toLowerCase().includes(key.toLowerCase())
    );
    
    return colors[matchedCategory] || colors['General Development'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Generating your weekly report...</p>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Child not found</p>
          <Link to="/dashboard">
            <Button className="gradient-emerald text-white">Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const recentActivities = getRecentActivities();
  const milestoneStats = getMilestoneStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Link to={`/activities/${childId}`}>
            <Button variant="outline" size="sm" className="btn-hover">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Activities
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl lg:text-4xl font-bold font-display bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Weekly Development Report
            </h1>
            <p className="text-slate-600 mt-1">
              {child.name} • {calculateAge(child.birth_date)} • Week of {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Child Overview */}
        <Card className="glass border-0">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                <Baby className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-800">{child.name}</h2>
                <p className="text-slate-600">Born {new Date(child.birth_date).toLocaleDateString()}</p>
              </div>
              <Button 
                onClick={generateWeeklyReport} 
                disabled={generating}
                className="btn-hover gradient-emerald text-white"
                data-testid="refresh-report-button"
              >
                {generating ? (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Refresh Report
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Week Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-2">{recentActivities.length}</div>
              <p className="text-slate-600">Activities This Week</p>
            </CardContent>
          </Card>
          
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-2">{milestoneStats.length}</div>
              <p className="text-slate-600">Milestone Categories</p>
            </CardContent>
          </Card>
          
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-2">
                {recentActivities.filter(a => a.ai_analysis && a.ai_analysis !== 'AI analysis unavailable').length}
              </div>
              <p className="text-slate-600">AI Analyzed</p>
            </CardContent>
          </Card>
        </div>

        {/* Milestone Breakdown */}
        {milestoneStats.length > 0 && (
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <span>Development Areas This Week</span>
              </CardTitle>
              <CardDescription>
                Breakdown of developmental milestones achieved
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="milestone-stats">
                {milestoneStats.map(([category, count]) => (
                  <div 
                    key={category}
                    className="text-center p-4 bg-white/50 rounded-lg border border-emerald-200/50"
                  >
                    <div className="text-2xl font-bold text-slate-800 mb-1">{count}</div>
                    <Badge 
                      className={`text-xs ${getMilestoneColor(category)}`}
                      data-testid={`milestone-category-${category}`}
                    >
                      {category}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Generated Report */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-emerald-600" />
              <span>AI-Generated Development Report</span>
            </CardTitle>
            <CardDescription>
              Personalized insights about {child.name}'s growth this week
            </CardDescription>
          </CardHeader>
          <CardContent>
            {generating ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                  <p className="text-slate-600">Generating personalized report...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="ai-report">
                <div className="bg-white/70 rounded-lg p-6 border border-emerald-200/50">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 mb-3">Weekly Development Summary</h3>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {report}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Report Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                  </div>
                  <div className="flex space-x-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="btn-hover"
                      onClick={() => window.print()}
                      data-testid="print-report-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Print Report
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activities Summary */}
        {recentActivities.length > 0 && (
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
                <FileText className="w-6 h-6 text-teal-600" />
                <span>Activities This Week ({recentActivities.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="recent-activities">
                {recentActivities.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-emerald-200/50">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{activity.description}</p>
                      <p className="text-sm text-slate-600">{new Date(activity.date).toLocaleDateString()}</p>
                    </div>
                    {activity.milestone_category && (
                      <Badge className={`${getMilestoneColor(activity.milestone_category)} ml-3`}>
                        {activity.milestone_category.split('.')[0]}
                      </Badge>
                    )}
                  </div>
                ))}
                
                {recentActivities.length > 5 && (
                  <div className="text-center pt-3">
                    <Link to={`/activities/${childId}`}>
                      <Button variant="outline" size="sm" className="btn-hover">
                        View All {recentActivities.length} Activities
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Activities Message */}
        {recentActivities.length === 0 && (
          <Card className="glass border-0">
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">No Activities This Week</h3>
              <p className="text-slate-500 mb-6">
                Record some activities to generate a meaningful weekly report for {child.name}.
              </p>
              <Link to={`/activities/${childId}`}>
                <Button className="btn-hover gradient-emerald text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  Add Activities
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WeeklyReports;
